// topicRecommendationEngine.ts
// Generates blog topic recommendations by analyzing gaps in existing content coverage

import { INDUSTRY_TAXONOMY } from './industryTaxonomy';

// ============================================================================
// Types
// ============================================================================

export interface TopicRecommendation {
  id: string;
  title: string;
  category: 'consumer' | 'business';
  subcategory: string;
  profession: string;
  industry: string;
  templateType: string;
}

export interface ExistingPost {
  title: string;
  primary_keyword: string | null;
  category_slug: string;
}

export interface CoverageStats {
  totalProfessions: number;
  coveredProfessions: number;
  consumerTopics: number;
  businessTopics: number;
  coveragePercentage: number;
}

interface ProfessionCoverage {
  profession: string;
  industry: string;
  hasConsumerContent: boolean;
  hasBusinessContent: boolean;
  consumerTemplatesCovered: string[];
  businessTemplatesCovered: string[];
}

// ============================================================================
// Topic Templates
// ============================================================================

const CONSUMER_TEMPLATES = [
  { type: 'how-to-find', template: 'How to Find a {profession} You Can Trust' },
  { type: 'questions-to-ask', template: '{number} Questions to Ask Before Hiring a {profession}' },
  { type: 'what-to-expect', template: 'What to Expect When Working with a {profession}' },
  { type: 'red-flags', template: '{number} Red Flags When Hiring a {profession}' },
  { type: 'preparation-guide', template: 'How to Prepare for Your First Meeting with a {profession}' },
  { type: 'cost-guide', template: 'Understanding {profession} Costs: What You Should Pay' },
  { type: 'comparison-guide', template: 'How to Compare {profession}s Before Making a Decision' },
  { type: 'signs-of-good', template: 'Signs of a Good vs Bad {profession}' },
  { type: 'when-to-hire', template: 'When Should You Hire a {profession}?' },
  { type: 'diy-vs-pro', template: 'DIY vs Hiring a {profession}: When Each Makes Sense' },
];

// Business templates focus on:
// 1. Why referrals/word-of-mouth matter (creates problem awareness)
// 2. Being referral-worthy (delivering great service)
// 3. The value of network connections (why being connected matters)
// 4. CTAs drive businesses to join Linked By Six so their connections can refer clients to them
//
// Mix of profession-specific AND general business topics for wider reach
const BUSINESS_TEMPLATES = [
  // Profession-specific templates
  { type: 'why-referrals-matter', template: 'Why Word-of-Mouth Is the Best Marketing for {profession}s' },
  { type: 'referral-worthy-service', template: 'How to Deliver Service Worth Recommending as a {profession}' },
  { type: 'client-experience', template: 'Creating a Client Experience That Generates Referrals for {profession}s' },
  { type: 'local-trust', template: 'How Local {profession}s Build Trust in Their Community' },
];

// General business templates (not profession-specific) - for wider audience reach
const GENERAL_BUSINESS_TEMPLATES = [
  { type: 'general-word-of-mouth', title: 'Why Word-of-Mouth Still Beats Digital Marketing for Service Businesses' },
  { type: 'general-trust-economy', title: 'The Trust Economy: Why Reputation Is Your Most Valuable Business Asset' },
  { type: 'general-referral-psychology', title: 'The Psychology of Referrals: Why People Recommend (And How to Earn It)' },
  { type: 'general-network-power', title: 'Your Personal Network Is Your Best Sales Team—Here\'s Why' },
  { type: 'general-client-loyalty', title: 'From Client to Advocate: How to Turn Happy Customers Into Referral Sources' },
  { type: 'general-service-excellence', title: 'Service Excellence: The Foundation of Every Successful Referral' },
  { type: 'general-local-business', title: 'Why Local Service Businesses Thrive on Community Trust' },
  { type: 'general-relationship-business', title: 'Relationship Over Transaction: The Service Business Advantage' },
  { type: 'general-first-impression', title: 'First Impressions That Lead to Referrals: A Service Provider\'s Guide' },
  { type: 'general-client-communication', title: 'How Great Communication Turns Clients Into Your Biggest Fans' },
  { type: 'general-reviews-vs-referrals', title: 'Online Reviews vs Personal Referrals: What Actually Drives Business' },
  { type: 'general-trust-signals', title: 'Trust Signals: What Makes Clients Confident to Recommend You' },
];

// ============================================================================
// Profession Subtopics - Related topics clients need to understand
// These are educational topics that complement the profession but aren't directly
// about hiring or working with the professional
// ============================================================================

const PROFESSION_SUBTOPICS: Record<string, Array<{ type: string; title: string }>> = {
  // Real Estate
  'Real Estate Agent/Broker': [
    { type: 'subtopic-mortgage', title: 'Understanding the Mortgage Process: A First-Time Buyer\'s Guide' },
    { type: 'subtopic-closing', title: 'What Happens at Closing: A Step-by-Step Guide' },
    { type: 'subtopic-inspection', title: 'Home Inspection 101: What Buyers Need to Know' },
    { type: 'subtopic-appraisal', title: 'Home Appraisals Explained: Why They Matter and What to Expect' },
    { type: 'subtopic-earnest', title: 'Earnest Money: What It Is and How Much You Need' },
    { type: 'subtopic-title', title: 'Title Insurance: Why You Need It and What It Covers' },
    { type: 'subtopic-contingencies', title: 'Understanding Contingencies in Real Estate Contracts' },
    { type: 'subtopic-preapproval', title: 'Mortgage Pre-Approval vs Pre-Qualification: What\'s the Difference?' },
  ],

  // Automotive
  'Auto Repair & Maintenance': [
    { type: 'subtopic-maintenance', title: 'Car Maintenance Schedule: What to Do and When' },
    { type: 'subtopic-warning-lights', title: 'Dashboard Warning Lights: What Each One Means' },
    { type: 'subtopic-tire-care', title: 'Tire Maintenance 101: Rotation, Alignment, and Replacement' },
    { type: 'subtopic-oil-change', title: 'Oil Changes: How Often and Why It Matters' },
    { type: 'subtopic-brake-signs', title: 'Signs Your Brakes Need Attention' },
    { type: 'subtopic-battery', title: 'Car Battery Care: Signs of Failure and Maintenance Tips' },
  ],
  'Auto Sales': [
    { type: 'subtopic-finance-lease', title: 'Financing vs Leasing: Which Is Right for You?' },
    { type: 'subtopic-trade-in', title: 'Maximizing Your Trade-In Value: What Dealers Look For' },
    { type: 'subtopic-new-used', title: 'New vs Used Cars: The Real Cost Comparison' },
    { type: 'subtopic-negotiation', title: 'Car Buying Negotiation: What\'s Actually Negotiable' },
    { type: 'subtopic-credit', title: 'How Your Credit Score Affects Auto Loan Rates' },
    { type: 'subtopic-warranty', title: 'Understanding Car Warranties: What\'s Covered and What\'s Not' },
  ],

  // Legal
  'Attorney/Lawyer': [
    { type: 'subtopic-legal-process', title: 'The Legal Process Explained: From Filing to Resolution' },
    { type: 'subtopic-retainer', title: 'Understanding Legal Retainers: How Attorney Fees Work' },
    { type: 'subtopic-discovery', title: 'Legal Discovery: What to Expect and How to Prepare' },
    { type: 'subtopic-settlement', title: 'Settlement vs Trial: Understanding Your Options' },
  ],
  'Family Law Attorney': [
    { type: 'subtopic-divorce-process', title: 'The Divorce Process: A Step-by-Step Timeline' },
    { type: 'subtopic-custody', title: 'Child Custody Basics: Types of Custody Explained' },
    { type: 'subtopic-mediation', title: 'Divorce Mediation: Is It Right for Your Situation?' },
    { type: 'subtopic-assets', title: 'Dividing Assets in Divorce: What You Need to Know' },
    { type: 'subtopic-support', title: 'Child Support and Alimony: How They\'re Calculated' },
  ],
  'Estate Planning Attorney': [
    { type: 'subtopic-will-trust', title: 'Wills vs Trusts: Which Do You Need?' },
    { type: 'subtopic-probate', title: 'Understanding Probate: What Happens After Someone Passes' },
    { type: 'subtopic-poa', title: 'Power of Attorney: Types and Why You Need One' },
    { type: 'subtopic-healthcare', title: 'Healthcare Directives: Making Your Wishes Known' },
    { type: 'subtopic-estate-tax', title: 'Estate Taxes: Thresholds and Planning Strategies' },
  ],
  'Personal Injury Attorney': [
    { type: 'subtopic-claims', title: 'The Personal Injury Claims Process Explained' },
    { type: 'subtopic-damages', title: 'Types of Damages in Personal Injury Cases' },
    { type: 'subtopic-statute', title: 'Statute of Limitations: Time Limits for Filing Claims' },
    { type: 'subtopic-insurance', title: 'Dealing with Insurance Companies After an Accident' },
  ],

  // Financial
  'Accountant/CPA': [
    { type: 'subtopic-deductions', title: 'Common Tax Deductions You Might Be Missing' },
    { type: 'subtopic-estimated', title: 'Quarterly Estimated Taxes: Who Needs to Pay and When' },
    { type: 'subtopic-audit', title: 'What Triggers an IRS Audit and How to Prepare' },
    { type: 'subtopic-records', title: 'Financial Records: What to Keep and For How Long' },
    { type: 'subtopic-business-entity', title: 'LLC vs S-Corp vs C-Corp: Which Is Right for Your Business?' },
  ],
  'Financial Advisor': [
    { type: 'subtopic-retirement', title: '401(k) vs IRA: Understanding Your Retirement Options' },
    { type: 'subtopic-investing', title: 'Investment Basics: Stocks, Bonds, and Mutual Funds Explained' },
    { type: 'subtopic-risk', title: 'Understanding Investment Risk Tolerance' },
    { type: 'subtopic-diversification', title: 'Portfolio Diversification: Why It Matters' },
    { type: 'subtopic-fees', title: 'Investment Fees: What You\'re Really Paying' },
  ],
  'Insurance Agent': [
    { type: 'subtopic-coverage', title: 'Understanding Insurance Coverage Types' },
    { type: 'subtopic-deductible', title: 'Deductibles and Premiums: Finding the Right Balance' },
    { type: 'subtopic-life', title: 'Term vs Whole Life Insurance: Which Do You Need?' },
    { type: 'subtopic-umbrella', title: 'Umbrella Insurance: Extra Protection Explained' },
    { type: 'subtopic-claims-process', title: 'Filing an Insurance Claim: Step-by-Step Guide' },
  ],
  'Mortgage Broker/Lender': [
    { type: 'subtopic-loan-types', title: 'Mortgage Types Explained: Conventional, FHA, VA, and More' },
    { type: 'subtopic-rates', title: 'Fixed vs Adjustable Rate Mortgages: Pros and Cons' },
    { type: 'subtopic-points', title: 'Mortgage Points: When Buying Down Your Rate Makes Sense' },
    { type: 'subtopic-refinance', title: 'When to Refinance Your Mortgage' },
    { type: 'subtopic-pmi', title: 'PMI Explained: What It Is and How to Avoid It' },
  ],

  // Medical
  'Physician/Doctor': [
    { type: 'subtopic-insurance-nav', title: 'Navigating Health Insurance: Understanding Your Coverage' },
    { type: 'subtopic-second-opinion', title: 'When and How to Get a Second Medical Opinion' },
    { type: 'subtopic-records-access', title: 'Accessing Your Medical Records: Your Rights Explained' },
  ],
  'Dentist': [
    { type: 'subtopic-dental-insurance', title: 'Understanding Dental Insurance: What\'s Covered' },
    { type: 'subtopic-preventive', title: 'Preventive Dental Care: What You Should Know' },
    { type: 'subtopic-dental-anxiety', title: 'Overcoming Dental Anxiety: Tips and Options' },
  ],
  'Physical Therapist': [
    { type: 'subtopic-recovery', title: 'What to Expect During Physical Therapy Recovery' },
    { type: 'subtopic-home-exercises', title: 'Home Exercise Programs: Maximizing Your PT Results' },
    { type: 'subtopic-pt-timeline', title: 'Physical Therapy Timelines: How Long Will Recovery Take?' },
  ],
  'Mental Health Therapist': [
    { type: 'subtopic-therapy-types', title: 'Types of Therapy: CBT, DBT, EMDR, and More Explained' },
    { type: 'subtopic-first-session', title: 'Your First Therapy Session: What to Expect' },
    { type: 'subtopic-mental-health-coverage', title: 'Mental Health Insurance Coverage: Know Your Benefits' },
  ],

  // Home Services
  'General Contractor': [
    { type: 'subtopic-permits', title: 'Building Permits: When You Need One and How to Get It' },
    { type: 'subtopic-contracts', title: 'Understanding Construction Contracts: Key Terms to Know' },
    { type: 'subtopic-timeline', title: 'Renovation Timelines: Setting Realistic Expectations' },
    { type: 'subtopic-change-orders', title: 'Change Orders: Managing Scope Changes During Construction' },
  ],
  'Plumber': [
    { type: 'subtopic-plumbing-emergency', title: 'Plumbing Emergencies: What to Do Before Help Arrives' },
    { type: 'subtopic-water-heater', title: 'Water Heater Types: Tank vs Tankless Comparison' },
    { type: 'subtopic-drain', title: 'Preventing Clogged Drains: Maintenance Tips' },
  ],
  'Electrician': [
    { type: 'subtopic-electrical-safety', title: 'Electrical Safety: Warning Signs of Problems' },
    { type: 'subtopic-panel', title: 'Electrical Panel Upgrades: When and Why You Need One' },
    { type: 'subtopic-smart-home', title: 'Smart Home Electrical Requirements: Planning Guide' },
  ],
  'HVAC Technician': [
    { type: 'subtopic-hvac-maintenance', title: 'HVAC Maintenance Schedule: Keeping Your System Efficient' },
    { type: 'subtopic-hvac-replacement', title: 'HVAC Replacement: Signs It\'s Time and What to Consider' },
    { type: 'subtopic-efficiency', title: 'SEER Ratings Explained: Understanding HVAC Efficiency' },
  ],
  'Roofer': [
    { type: 'subtopic-roof-materials', title: 'Roofing Materials Compared: Shingles, Metal, Tile, and More' },
    { type: 'subtopic-roof-inspection', title: 'Roof Inspection: What Professionals Look For' },
    { type: 'subtopic-roof-lifespan', title: 'Roof Lifespan: How Long Should Your Roof Last?' },
  ],

  // Pet Services
  'Veterinarian': [
    { type: 'subtopic-pet-insurance', title: 'Pet Insurance: Is It Worth It? A Complete Guide' },
    { type: 'subtopic-vaccinations', title: 'Pet Vaccination Schedules: What Your Pet Needs and When' },
    { type: 'subtopic-pet-emergency', title: 'Pet Emergencies: Signs You Need Immediate Vet Care' },
  ],

  // Childcare
  'Daycare/Childcare Provider': [
    { type: 'subtopic-daycare-types', title: 'Types of Childcare: Daycare, Nanny, Au Pair Compared' },
    { type: 'subtopic-childcare-cost', title: 'Understanding Childcare Costs: What to Budget' },
    { type: 'subtopic-licensing', title: 'Childcare Licensing: What It Means and Why It Matters' },
  ],

  // Senior Care
  'Home Health Aide': [
    { type: 'subtopic-care-levels', title: 'Levels of Senior Care: From Independent to Skilled Nursing' },
    { type: 'subtopic-medicare', title: 'Medicare and Home Health: What\'s Covered' },
    { type: 'subtopic-caregiver-burnout', title: 'Caregiver Burnout: Signs and Prevention Strategies' },
  ],

  // Beauty & Personal Care
  'Hair Salon/Stylist': [
    { type: 'subtopic-hair-care', title: 'Hair Care Between Appointments: Maintaining Your Style' },
    { type: 'subtopic-color', title: 'Hair Color Options: Permanent, Semi-Permanent, and Highlights Explained' },
  ],

  // Education
  'Tutor': [
    { type: 'subtopic-learning-styles', title: 'Learning Styles: Helping Your Child Study Effectively' },
    { type: 'subtopic-test-prep', title: 'Standardized Test Prep: SAT, ACT, and Beyond' },
  ],

  // Fitness
  'Personal Trainer': [
    { type: 'subtopic-fitness-goals', title: 'Setting Realistic Fitness Goals: A Beginner\'s Guide' },
    { type: 'subtopic-nutrition', title: 'Nutrition Basics for Fitness: What to Eat and When' },
    { type: 'subtopic-recovery', title: 'Exercise Recovery: Rest Days and Active Recovery Explained' },
  ],
};

// Map industry to blog subcategory
const INDUSTRY_TO_CONSUMER_SUBCATEGORY: Record<string, string> = {
  'Legal Services': 'professional-services',
  'Medical & Healthcare': 'personal-services',
  'Automotive Services': 'automotive',
  'Home Services': 'home-services',
  'Construction & Contractors': 'home-services',
  'Financial Services': 'professional-services',
  'Real Estate': 'professional-services',
  'Beauty & Personal Care': 'personal-services',
  'Professional Services': 'professional-services',
  'Education & Training': 'personal-services',
  'Fitness & Wellness': 'personal-services',
  'Pet Services': 'personal-services',
  'Senior & Care Services': 'personal-services',
  'Childcare & Family Services': 'personal-services',
  'Technology & Electronics': 'home-services',
  'Travel & Transportation': 'professional-services',
  'Funeral & Memorial Services': 'events-special-occasions',
  'Specialty Services': 'professional-services',
};

const INDUSTRY_TO_BUSINESS_SUBCATEGORY: Record<string, string> = {
  'Legal Services': 'service-business-operations',
  'Medical & Healthcare': 'customer-trust-retention',
  'Automotive Services': 'lead-generation',
  'Home Services': 'lead-generation',
  'Construction & Contractors': 'lead-generation',
  'Financial Services': 'networking-relationships',
  'Real Estate': 'networking-relationships',
  'Beauty & Personal Care': 'customer-trust-retention',
  'Professional Services': 'networking-relationships',
  'Education & Training': 'customer-trust-retention',
  'Fitness & Wellness': 'customer-trust-retention',
  'Pet Services': 'customer-trust-retention',
  'Senior & Care Services': 'customer-trust-retention',
  'Childcare & Family Services': 'customer-trust-retention',
  'Technology & Electronics': 'lead-generation',
  'Travel & Transportation': 'customer-trust-retention',
  'Funeral & Memorial Services': 'community-marketing',
  'Specialty Services': 'lead-generation',
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get all professions from the taxonomy
 */
export function getAllProfessions(): Array<{ profession: string; industry: string }> {
  const professions: Array<{ profession: string; industry: string }> = [];

  for (const industryItem of INDUSTRY_TAXONOMY) {
    for (const prof of industryItem.professions) {
      // Skip "Other" entries as they're too generic
      if (prof.name !== 'Other') {
        professions.push({
          profession: prof.name,
          industry: industryItem.industry,
        });
      }
    }
  }

  return professions;
}

/**
 * Normalize text for comparison (lowercase, remove special chars)
 */
function normalizeText(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
}

/**
 * Check if a profession is mentioned in existing content
 */
function professionMentionedIn(profession: string, text: string): boolean {
  const normalizedProfession = normalizeText(profession);
  const normalizedText = normalizeText(text);

  // Direct match
  if (normalizedText.includes(normalizedProfession)) {
    return true;
  }

  // Handle plurals (simple)
  const plural = normalizedProfession + 's';
  if (normalizedText.includes(plural)) {
    return true;
  }

  // Handle common variations
  const variations: Record<string, string[]> = {
    'attorney/lawyer': ['attorney', 'lawyer', 'legal'],
    'physician/doctor': ['physician', 'doctor', 'medical'],
    'real estate agent/broker': ['real estate agent', 'realtor', 'real estate broker'],
    'accountant/cpa': ['accountant', 'cpa', 'certified public accountant'],
    'hair salon/stylist': ['hair salon', 'hair stylist', 'hairdresser'],
    'auto repair & maintenance': ['auto repair', 'mechanic', 'auto mechanic'],
  };

  const profVariations = variations[normalizedProfession] || [];
  for (const variation of profVariations) {
    if (normalizedText.includes(variation)) {
      return true;
    }
  }

  return false;
}

/**
 * Check which template type an existing post likely covers
 */
function detectTemplateType(title: string, category: 'consumer' | 'business'): string | null {
  const normalizedTitle = normalizeText(title);

  if (category === 'consumer') {
    if (normalizedTitle.includes('how to find') || normalizedTitle.includes('finding')) {
      return 'how-to-find';
    }
    if (normalizedTitle.includes('question') || normalizedTitle.includes('ask')) {
      return 'questions-to-ask';
    }
    if (normalizedTitle.includes('what to expect') || normalizedTitle.includes('expect when')) {
      return 'what-to-expect';
    }
    if (normalizedTitle.includes('red flag') || normalizedTitle.includes('warning sign')) {
      return 'red-flags';
    }
    if (normalizedTitle.includes('prepare') || normalizedTitle.includes('first meeting')) {
      return 'preparation-guide';
    }
    if (normalizedTitle.includes('cost') || normalizedTitle.includes('price') || normalizedTitle.includes('pay')) {
      return 'cost-guide';
    }
    if (normalizedTitle.includes('compare') || normalizedTitle.includes('choosing')) {
      return 'comparison-guide';
    }
    if (normalizedTitle.includes('signs of') || normalizedTitle.includes('good vs bad')) {
      return 'signs-of-good';
    }
    if (normalizedTitle.includes('when to hire') || normalizedTitle.includes('when should')) {
      return 'when-to-hire';
    }
    if (normalizedTitle.includes('diy') || normalizedTitle.includes('yourself')) {
      return 'diy-vs-pro';
    }
  } else {
    // Business templates (profession-specific)
    if (normalizedTitle.includes('word of mouth') || normalizedTitle.includes('word-of-mouth')) {
      return 'why-referrals-matter';
    }
    if (normalizedTitle.includes('worth recommending') || normalizedTitle.includes('referral worthy') || normalizedTitle.includes('service worth')) {
      return 'referral-worthy-service';
    }
    if (normalizedTitle.includes('client experience') || normalizedTitle.includes('customer experience')) {
      return 'client-experience';
    }
    if (normalizedTitle.includes('local') && normalizedTitle.includes('trust')) {
      return 'local-trust';
    }

    // General business templates
    if (normalizedTitle.includes('trust economy') || normalizedTitle.includes('reputation') && normalizedTitle.includes('asset')) {
      return 'general-trust-economy';
    }
    if (normalizedTitle.includes('psychology of referral') || normalizedTitle.includes('why people recommend')) {
      return 'general-referral-psychology';
    }
    if (normalizedTitle.includes('personal network') && normalizedTitle.includes('sales')) {
      return 'general-network-power';
    }
    if (normalizedTitle.includes('client to advocate') || normalizedTitle.includes('happy customers')) {
      return 'general-client-loyalty';
    }
    if (normalizedTitle.includes('service excellence') || normalizedTitle.includes('foundation')) {
      return 'general-service-excellence';
    }
    if (normalizedTitle.includes('local service business') && normalizedTitle.includes('community')) {
      return 'general-local-business';
    }
    if (normalizedTitle.includes('relationship over transaction')) {
      return 'general-relationship-business';
    }
    if (normalizedTitle.includes('first impression') && normalizedTitle.includes('referral')) {
      return 'general-first-impression';
    }
    if (normalizedTitle.includes('communication') && normalizedTitle.includes('fans')) {
      return 'general-client-communication';
    }
    if (normalizedTitle.includes('reviews vs') || normalizedTitle.includes('personal referral')) {
      return 'general-reviews-vs-referrals';
    }
    if (normalizedTitle.includes('trust signal') && normalizedTitle.includes('confident')) {
      return 'general-trust-signals';
    }
  }

  return null;
}

/**
 * Analyze coverage of existing blog posts
 */
export function analyzeCoverage(existingPosts: ExistingPost[]): Map<string, ProfessionCoverage> {
  const allProfessions = getAllProfessions();
  const coverageMap = new Map<string, ProfessionCoverage>();

  // Initialize coverage for all professions
  for (const { profession, industry } of allProfessions) {
    coverageMap.set(profession, {
      profession,
      industry,
      hasConsumerContent: false,
      hasBusinessContent: false,
      consumerTemplatesCovered: [],
      businessTemplatesCovered: [],
    });
  }

  // Analyze existing posts
  for (const post of existingPosts) {
    const category = post.category_slug === 'business' ? 'business' : 'consumer';
    const searchText = `${post.title} ${post.primary_keyword || ''}`;

    for (const { profession } of allProfessions) {
      if (professionMentionedIn(profession, searchText)) {
        const coverage = coverageMap.get(profession)!;

        if (category === 'consumer') {
          coverage.hasConsumerContent = true;
          const templateType = detectTemplateType(post.title, 'consumer');
          if (templateType && !coverage.consumerTemplatesCovered.includes(templateType)) {
            coverage.consumerTemplatesCovered.push(templateType);
          }
        } else {
          coverage.hasBusinessContent = true;
          const templateType = detectTemplateType(post.title, 'business');
          if (templateType && !coverage.businessTemplatesCovered.includes(templateType)) {
            coverage.businessTemplatesCovered.push(templateType);
          }
        }
      }
    }
  }

  return coverageMap;
}

/**
 * Calculate coverage statistics
 */
export function calculateCoverageStats(
  coverageMap: Map<string, ProfessionCoverage>,
  existingPosts: ExistingPost[]
): CoverageStats {
  let coveredProfessions = 0;

  for (const coverage of coverageMap.values()) {
    if (coverage.hasConsumerContent || coverage.hasBusinessContent) {
      coveredProfessions++;
    }
  }

  const consumerTopics = existingPosts.filter(p => p.category_slug === 'consumer').length;
  const businessTopics = existingPosts.filter(p => p.category_slug === 'business').length;

  return {
    totalProfessions: coverageMap.size,
    coveredProfessions,
    consumerTopics,
    businessTopics,
    coveragePercentage: Math.round((coveredProfessions / coverageMap.size) * 100),
  };
}

/**
 * Generate a random number for templates
 */
function getRandomNumber(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Format profession for display (handle articles a/an)
 */
function formatProfessionWithArticle(profession: string): string {
  const vowels = ['a', 'e', 'i', 'o', 'u'];
  const firstChar = profession.charAt(0).toLowerCase();
  const article = vowels.includes(firstChar) ? 'an' : 'a';
  return `${article} ${profession}`;
}

/**
 * Generate topic title from template
 */
function generateTopicTitle(template: string, profession: string): string {
  let title = template
    .replace('{profession}', profession)
    .replace('{number}', String(getRandomNumber(5, 10)));

  // Handle "a/an" for the "Find a {profession}" templates
  if (title.includes('Find a ')) {
    const vowels = ['a', 'e', 'i', 'o', 'u'];
    const afterFind = title.split('Find a ')[1];
    if (afterFind && vowels.includes(afterFind.charAt(0).toLowerCase())) {
      title = title.replace('Find a ', 'Find an ');
    }
  }

  return title;
}

/**
 * Generate topic recommendations based on coverage gaps
 * @param professionFilter - If provided, only generate topics for these specific professions
 */
export function generateRecommendations(
  coverageMap: Map<string, ProfessionCoverage>,
  count: number = 20,
  categoryFilter?: 'consumer' | 'business' | 'mixed',
  professionFilter?: Set<string>
): TopicRecommendation[] {
  const recommendations: TopicRecommendation[] = [];
  const usedCombinations = new Set<string>();

  // Get all uncovered/undercovered profession-template combinations
  const potentialTopics: Array<{
    profession: string;
    industry: string;
    category: 'consumer' | 'business';
    template: { type: string; template: string };
    priority: number; // Lower is higher priority
  }> = [];

  for (const [profession, coverage] of coverageMap) {
    // If profession filter is set, skip professions not in the filter
    if (professionFilter && professionFilter.size > 0 && !professionFilter.has(profession)) {
      continue;
    }

    // Consumer topics
    if (categoryFilter !== 'business') {
      for (const template of CONSUMER_TEMPLATES) {
        if (!coverage.consumerTemplatesCovered.includes(template.type)) {
          potentialTopics.push({
            profession,
            industry: coverage.industry,
            category: 'consumer',
            template,
            // Higher priority for completely uncovered professions
            priority: coverage.hasConsumerContent ? 2 : 1,
          });
        }
      }
    }

    // Business topics (profession-specific)
    if (categoryFilter !== 'consumer') {
      for (const template of BUSINESS_TEMPLATES) {
        if (!coverage.businessTemplatesCovered.includes(template.type)) {
          potentialTopics.push({
            profession,
            industry: coverage.industry,
            category: 'business',
            template,
            priority: coverage.hasBusinessContent ? 2 : 1,
          });
        }
      }
    }
  }

  // Sort by priority and shuffle within same priority
  potentialTopics.sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority - b.priority;
    }
    return Math.random() - 0.5;
  });

  // Select topics ensuring variety
  const selectedByCategory = { consumer: 0, business: 0 };
  const selectedByIndustry = new Map<string, number>();
  const selectedByTemplate = new Map<string, number>();

  // First, add some general business topics if business category is included
  if (categoryFilter !== 'consumer') {
    // Shuffle general templates and pick a few
    const shuffledGeneral = [...GENERAL_BUSINESS_TEMPLATES].sort(() => Math.random() - 0.5);
    const generalCount = categoryFilter === 'business' ? 6 : 3; // More if business-only filter

    for (let i = 0; i < Math.min(generalCount, shuffledGeneral.length); i++) {
      const generalTopic = shuffledGeneral[i];
      if (!usedCombinations.has(generalTopic.type)) {
        recommendations.push({
          id: crypto.randomUUID(),
          title: generalTopic.title,
          category: 'business',
          subcategory: 'lead-generation', // General business subcategory
          profession: 'Service Providers', // General
          industry: 'General',
          templateType: generalTopic.type,
        });
        usedCombinations.add(generalTopic.type);
        selectedByCategory.business++;
      }
    }
  }

  // Add profession subtopics for consumer category
  // These are educational topics related to the profession (e.g., "Mortgage Process" for Real Estate)
  if (categoryFilter !== 'business') {
    for (const [profession, coverage] of coverageMap) {
      // If profession filter is set, skip professions not in the filter
      if (professionFilter && professionFilter.size > 0 && !professionFilter.has(profession)) {
        continue;
      }

      const subtopics = PROFESSION_SUBTOPICS[profession];
      if (subtopics) {
        // Shuffle subtopics to get variety
        const shuffledSubtopics = [...subtopics].sort(() => Math.random() - 0.5);

        for (const subtopic of shuffledSubtopics) {
          const combinationKey = `${profession}-${subtopic.type}`;
          if (usedCombinations.has(combinationKey)) continue;

          potentialTopics.push({
            profession,
            industry: coverage.industry,
            category: 'consumer',
            template: { type: subtopic.type, template: subtopic.title },
            priority: 3, // Lower priority than main profession topics
          });
        }
      }
    }
  }

  // Re-sort after adding subtopics
  potentialTopics.sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority - b.priority;
    }
    return Math.random() - 0.5;
  });

  // Then add profession-specific topics (including subtopics)
  for (const potential of potentialTopics) {
    if (recommendations.length >= count) break;

    const combinationKey = `${potential.profession}-${potential.template.type}-${potential.category}`;
    if (usedCombinations.has(combinationKey)) continue;

    // Ensure diversity - don't over-represent any category, industry, or template
    const industryCount = selectedByIndustry.get(potential.industry) || 0;
    const templateCount = selectedByTemplate.get(potential.template.type) || 0;

    // Skip if we have too many from same industry (max 6 per industry to allow subtopics)
    if (industryCount >= 6) continue;

    // Skip if we have too many of same template type (max 3, but subtopics are unique)
    if (!potential.template.type.startsWith('subtopic-') && templateCount >= 3) continue;

    // For mixed mode, try to balance categories
    if (categoryFilter === 'mixed' || !categoryFilter) {
      const targetBalance = count / 2;
      if (selectedByCategory[potential.category] >= targetBalance + 2) continue;
    }

    // For subtopics, use the title directly; for templates, generate from template
    const title = potential.template.type.startsWith('subtopic-')
      ? potential.template.template
      : generateTopicTitle(potential.template.template, potential.profession);

    const subcategory = potential.category === 'consumer'
      ? INDUSTRY_TO_CONSUMER_SUBCATEGORY[potential.industry] || 'professional-services'
      : INDUSTRY_TO_BUSINESS_SUBCATEGORY[potential.industry] || 'lead-generation';

    recommendations.push({
      id: crypto.randomUUID(),
      title,
      category: potential.category,
      subcategory,
      profession: potential.profession,
      industry: potential.industry,
      templateType: potential.template.type,
    });

    usedCombinations.add(combinationKey);
    selectedByCategory[potential.category]++;
    selectedByIndustry.set(potential.industry, industryCount + 1);
    selectedByTemplate.set(potential.template.type, templateCount + 1);
  }

  // Final shuffle to mix up the order
  return recommendations.sort(() => Math.random() - 0.5);
}

/**
 * Main function to get topic recommendations
 * @param professionFilter - If provided, only generate topics for these specific professions
 */
export function getTopicRecommendations(
  existingPosts: ExistingPost[],
  count: number = 20,
  categoryFilter?: 'consumer' | 'business' | 'mixed',
  professionFilter?: Set<string>
): {
  recommendations: TopicRecommendation[];
  stats: CoverageStats;
} {
  const coverageMap = analyzeCoverage(existingPosts);
  const stats = calculateCoverageStats(coverageMap, existingPosts);
  const recommendations = generateRecommendations(coverageMap, count, categoryFilter, professionFilter);

  return { recommendations, stats };
}
